import type { Campaign } from 'src/pages/client-management/types/client-management.types';
import type { Client, ClientUser } from 'src/pages/client-management/types/client.types';
import type {
  CampaignEmailDayOfWeek,
  CampaignEmailFormValues,
  CampaignEmailRecipientWrite,
  CampaignEmailSchedule,
  CampaignEmailScheduleWritePayload,
  SelectedCampaignEntry,
} from '../types/campaign-email.types';
import { CAMPAIGN_EMAIL_DAY_OF_WEEK_OPTIONS, DEFAULT_SECTIONS_CONFIG } from './campaign-email.constants';

export function campaignToSelectedEntry(campaign: Campaign): SelectedCampaignEntry {
  return {
    source: campaign.account?.source ?? campaign.platform,
    line_item_id: campaign.ext_campaign_id,
  };
}

export function selectedEntryMatchesCampaign(
  entry: SelectedCampaignEntry,
  campaign: Campaign
): boolean {
  const entityId = entry.line_item_id ?? entry.campaign_id ?? '';
  const source = campaign.account?.source ?? campaign.platform;
  return entry.source === source && entityId === campaign.ext_campaign_id;
}

export function resolveSelectedCampaignIds(
  entries: SelectedCampaignEntry[],
  campaigns: Campaign[]
): string[] {
  return campaigns
    .filter((campaign) => entries.some((entry) => selectedEntryMatchesCampaign(entry, campaign)))
    .map((campaign) => campaign.id);
}

export function buildRecipientsPayload(
  portalContactIds: string[],
  manualRecipients: Array<{ name: string; email: string }>
): CampaignEmailRecipientWrite[] {
  const portalRecipients: CampaignEmailRecipientWrite[] = portalContactIds.map((clientUserId) => ({
    recipient_type: 'portal_contact',
    client_user: clientUserId,
  }));

  const manualEntries: CampaignEmailRecipientWrite[] = manualRecipients
    .filter((recipient) => recipient.email.trim() !== '')
    .map((recipient) => ({
      recipient_type: 'manual',
      manual_name: recipient.name.trim() || null,
      manual_email: recipient.email.trim(),
    }));

  return [...portalRecipients, ...manualEntries];
}

export function formValuesToWritePayload(
  values: CampaignEmailFormValues,
  campaigns: Campaign[]
): CampaignEmailScheduleWritePayload {
  const selectedCampaigns = campaigns
    .filter((campaign) => values.selectedCampaignIds.includes(campaign.id))
    .map(campaignToSelectedEntry);

  return {
    name: values.name.trim(),
    client: values.clientId || null,
    selected_campaigns: selectedCampaigns,
    frequency: values.frequency,
    day_of_week: values.dayOfWeek,
    start_date: values.frequency === 'daily' ? values.startDate || null : null,
    end_date: values.frequency === 'daily' ? values.endDate || null : null,
    sections_config: values.sectionsConfig,
    ai_narrative_enabled: values.aiNarrativeEnabled,
    custom_prompt_instructions: values.aiNarrativeEnabled
      ? values.customPromptInstructions.trim() || null
      : null,
    branding_override: values.useBrandingOverride
      ? values.brandingOverride.trim() || null
      : null,
    schedule_status: values.scheduleStatus,
    recipients: buildRecipientsPayload(values.portalContactIds, values.manualRecipients),
  };
}

export function scheduleToFormValues(
  schedule: CampaignEmailSchedule,
  campaigns: Campaign[]
): CampaignEmailFormValues {
  const activeRecipients = schedule.recipients.filter(
    (recipient) => !recipient.status || recipient.status === 'active'
  );

  const portalContactIds = activeRecipients
    .filter((recipient) => recipient.recipient_type === 'portal_contact' && recipient.client_user)
    .map((recipient) => recipient.client_user as string);

  const manualRecipients = activeRecipients
    .filter((recipient) => recipient.recipient_type === 'manual')
    .map((recipient) => ({
      name: recipient.manual_name ?? '',
      email: recipient.manual_email ?? '',
    }));

  const hasBrandingOverride = Boolean(schedule.branding_override);
  const dayOfWeek = (
    schedule.day_of_week >= 0 && schedule.day_of_week <= 6
      ? schedule.day_of_week
      : 0
  ) as CampaignEmailDayOfWeek;

  return {
    name: schedule.name,
    clientId: schedule.client ?? '',
    selectedCampaignIds: resolveSelectedCampaignIds(schedule.selected_campaigns, campaigns),
    frequency: schedule.frequency,
    dayOfWeek,
    startDate: schedule.start_date ?? '',
    endDate: schedule.end_date ?? '',
    sectionsConfig: {
      summary_metrics: schedule.sections_config?.summary_metrics?.length
        ? schedule.sections_config.summary_metrics
        : DEFAULT_SECTIONS_CONFIG.summary_metrics,
      include_chart: schedule.sections_config?.include_chart ?? true,
      chart_metrics: (() => {
        const metrics = schedule.sections_config?.chart_metrics;
        if (Array.isArray(metrics) && metrics.length > 0) {
          return metrics.slice(0, 2);
        }
        const legacy = schedule.sections_config?.chart_metric;
        return legacy ? [legacy] : [...DEFAULT_SECTIONS_CONFIG.chart_metrics];
      })(),
    },
    aiNarrativeEnabled: schedule.ai_narrative_enabled,
    customPromptInstructions: schedule.custom_prompt_instructions ?? '',
    brandingOverride: schedule.branding_override ?? '',
    useBrandingOverride: hasBrandingOverride,
    portalContactIds,
    manualRecipients,
    scheduleStatus: schedule.schedule_status,
  };
}

export function getClientPortalUsers(client: Client | undefined): ClientUser[] {
  return client?.users?.filter((user) => user.status === 'active') ?? [];
}

export function formatFrequencyLabel(frequency: string): string {
  switch (frequency) {
    case 'daily':
      return 'Daily';
    case 'weekly':
      return 'Weekly';
    case 'biweekly':
      return 'Bi-weekly';
    case 'monthly':
      return 'Monthly';
    default:
      return frequency;
  }
}

export function formatDayOfWeekLabel(dayOfWeek: number | null | undefined): string {
  const match = CAMPAIGN_EMAIL_DAY_OF_WEEK_OPTIONS.find((option) => option.value === dayOfWeek);
  return match?.label ?? 'Monday';
}

export function summariseSelectedCampaigns(
  selectedCampaigns: SelectedCampaignEntry[]
): string {
  if (selectedCampaigns.length === 0) {
    return 'No campaigns';
  }
  if (selectedCampaigns.length === 1) {
    return '1 campaign';
  }
  return `${selectedCampaigns.length} campaigns`;
}
